import { QueryClient, QueryFunction } from "@tanstack/react-query";

const TOKEN_KEY = "dotnetbot.jwt";
// Impersonation tabs store their token in sessionStorage so they never
// overwrite the Platform Admin's own localStorage token. sessionStorage is
// tab-specific and is automatically cleared when the tab is closed.
const SESSION_TOKEN_KEY = "dotnetbot.jwt.session";

export function getAuthToken(): string | null {
  try {
    // sessionStorage wins — this tab is an impersonation session.
    return sessionStorage.getItem(SESSION_TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  } catch { return null; }
}
export function setAuthToken(t: string | null) {
  try {
    // If this tab already has an impersonation token in sessionStorage,
    // keep subsequent writes (e.g. token refresh) in sessionStorage too.
    const isImpersonationTab = !!sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (isImpersonationTab) {
      if (t) sessionStorage.setItem(SESSION_TOKEN_KEY, t);
      else sessionStorage.removeItem(SESSION_TOKEN_KEY);
    } else {
      if (t) localStorage.setItem(TOKEN_KEY, t);
      else localStorage.removeItem(TOKEN_KEY);
    }
  } catch {}
}

/** Write an impersonation token into this tab's sessionStorage. */
export function setImpersonationToken(t: string): void {
  try { sessionStorage.setItem(SESSION_TOKEN_KEY, t); } catch {}
}

// ── Refresh token storage ─────────────────────────────────────────────────────
// Mirrors the impersonation-awareness of setAuthToken/getAuthToken:
// impersonation tabs store their tokens in sessionStorage so they never
// overwrite the Platform Admin's own localStorage tokens.

const REFRESH_TOKEN_KEY = "dotnetbot.refresh";
const SESSION_REFRESH_TOKEN_KEY = "dotnetbot.refresh.session";

export function getRefreshToken(): string | null {
  try {
    return (
      sessionStorage.getItem(SESSION_REFRESH_TOKEN_KEY) ||
      localStorage.getItem(REFRESH_TOKEN_KEY) ||
      null
    );
  } catch {
    return null;
  }
}

export function setRefreshToken(t: string | null): void {
  try {
    const isImpersonationTab = !!sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (isImpersonationTab) {
      if (t) sessionStorage.setItem(SESSION_REFRESH_TOKEN_KEY, t);
      else   sessionStorage.removeItem(SESSION_REFRESH_TOKEN_KEY);
    } else {
      if (t) localStorage.setItem(REFRESH_TOKEN_KEY, t);
      else   localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {}
}

/** Removes the refresh token from every storage location unconditionally. */
export function clearRefreshToken(): void {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_REFRESH_TOKEN_KEY);
  } catch {}
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const h: Record<string, string> = { ...extra };
  const t = getAuthToken();
  if (t) h["Authorization"] = `Bearer ${t}`;
  return h;
}

const STATUS_FALLBACKS: Record<number, string> = {
  400: "The request couldn't be processed. Please check your input and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to perform this action.",
  404: "The requested item could not be found.",
  409: "This conflicts with something that already exists.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "Something went wrong on the server. Please try again.",
  502: "The server is temporarily unavailable. Please try again shortly.",
  503: "The service is temporarily unavailable. Please try again shortly.",
};

export function friendlyErrorMessage(status: number, rawBody: string): string {
  // API errors are JSON like {"message":"..."} (sometimes {"error":"..."} or
  // ASP.NET problem-details {"title":"..."}). Surface just the human text —
  // never raw JSON or a status-code prefix.
  const text = (rawBody || "").trim();
  if (text) {
    try {
      const j = JSON.parse(text);
      const msg = j?.message ?? j?.error_description ?? j?.error ?? j?.detail ?? j?.title;
      if (typeof msg === "string" && msg.trim()) return msg.trim();
    } catch {
      // Not JSON — show it only if it looks like plain human text (not HTML).
      if (!text.startsWith("<") && !text.startsWith("{") && text.length <= 300) return text;
    }
  }
  return STATUS_FALLBACKS[status] ?? "An unexpected error occurred. Please try again.";
}

/** Structured field-level validation error, thrown when the API returns { errors: {...} }. */
export class ValidationError extends Error {
  /** Field-keyed error messages from the API, e.g. { userId: ["User not found."] } */
  fieldErrors: Record<string, string[]>;
  constructor(fieldErrors: Record<string, string[]>) {
    // Build a human-readable summary from the first field's first message.
    const firstField  = Object.keys(fieldErrors)[0] ?? "";
    const firstMsg    = fieldErrors[firstField]?.[0] ?? "Validation failed.";
    super(firstMsg);
    this.name        = "ValidationError";
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Extract field-level errors from any thrown value.
 * Returns null when the error is not a ValidationError.
 */
export function extractFieldErrors(err: unknown): Record<string, string[]> | null {
  if (err instanceof ValidationError) return err.fieldErrors;
  return null;
}

/**
 * Pick the first message for a specific field, or fall back to the first
 * message from any field, or fall back to the Error's own message.
 */
export function fieldError(
  err: unknown,
  field: string,
): string | null {
  const fe = extractFieldErrors(err);
  if (!fe) return null;
  return (fe[field]?.[0] ?? Object.values(fe).flat()[0]) ?? null;
}

export async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // Try structured JSON first — backend compliance endpoints return
    // { errors: { fieldName: ["message"] } } for validation failures.
    const text = await res.text().catch(() => "");
    try {
      const j = JSON.parse(text);
      if (j && typeof j.errors === "object" && !Array.isArray(j.errors)) {
        // Normalise: values may be strings or string arrays.
        const fe: Record<string, string[]> = {};
        for (const [k, v] of Object.entries(j.errors)) {
          fe[k] = Array.isArray(v) ? (v as string[]) : [String(v)];
        }
        if (Object.keys(fe).length > 0) throw new ValidationError(fe);
      }
    } catch (inner) {
      if (inner instanceof ValidationError) throw inner;
      // Not structured — fall through to friendly message.
    }
    throw new Error(friendlyErrorMessage(res.status, text));
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: authHeaders(data ? { "Content-Type": "application/json" } : {}),
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      headers: authHeaders(),
      credentials: "include",
    });
    if (unauthorizedBehavior === "returnNull" && res.status === 401) return null;
    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      // staleTime: Infinity means data is never considered stale, so the
      // default refetchOnMount: true would never actually refetch on mount
      // (because "refetch if stale" is never triggered). Setting
      // refetchOnMount: "always" overrides this: every navigation to a page
      // fetches fresh data from the server, so sidebar navigation updates
      // correctly without a manual browser refresh.
      staleTime: Infinity,
      refetchOnMount: "always",
      retry: false,
    },
    mutations: { retry: false },
  },
});
